// Import the mixins
import { BugsnagService } from '@/mixins/services/bugsnag/BugsnagService'
import { SpinnerMethods } from '@/mixins/helpers/SpinnerMethods'
import { GenerateUID } from '@/mixins/helpers/GenerateUID'

// Import the repository
import { ResidentsRepository } from '@/repositories/residents/ResidentsRepository'

// Import firebase
import { propertiesModel, residentsModel, companiesModel, emailsModel } from '@/firebase/firebase-config'

export const CreateResident = {

    /*
	**  Register the mixins
	*/

	mixins: [

        BugsnagService,
        SpinnerMethods,
        GenerateUID,
        ResidentsRepository

    ],

    /*
	**  Set up the data
	*/

	data: () => ({

        processing: false,
        create_resident_error: false,
        set_uid_complete: false,
        stripe_customer_retry_count: 0,
        new_resident_props_set: false,
        create_property_resident_complete: false,
        update_new_resident_unit_complete: false,
        update_new_resident_property_complete: false,
        create_resident_stripe_complete: false,
        determine_portal_enrollment_complete: false,
        create_resident_complete: false,
        update_new_resident_company_complete: false,
        email_sent: false

    }),

    /*
    **  Watch em'
    */

    watch: {

        create_resident_error: {

            immediate: true, // Load on created
            handler(create_resident_error) {

                /*
                **  Handle the error
                **  @method: @here
                */

                if (create_resident_error) this.handleCreateResidentError();

            }

        },

        /*
        **  Step 1
        **  @prop: @here
        */

        set_uid_complete: {

            immediate: true, // Load on created
            handler(set_uid_complete) {

                /*
                **  Set the new resident props
                **  @method: @here
                */

                if (set_uid_complete && !this.create_resident_error) this.setNewResidentProps();

            }

        },

        /*
        **  Step 2
        **  @prop: @here
        */

        new_resident_props_set: {

            immediate: true, // Load on created
            handler(new_resident_props_set) {

                /*
                **  If enrolled user, determine portal enrollment
                **  @method: @here
                */

                if (new_resident_props_set && !this.create_resident_error && this.new_resident.attributes.site_enrollment.enroll_resident) this.determinePortalEnrollment();

                /*
                **  If non-enrolled user, create property resident
                **  @method: @here
                */

                if (new_resident_props_set && !this.create_resident_error && !this.new_resident.attributes.site_enrollment.enroll_resident) this.create_resident_stripe_complete = true;

            }

        },

        /*
        **  Step 3
        **  @prop: @here
        */
        
        determine_portal_enrollment_complete: {

            immediate: true, // Load on created
            handler(determine_portal_enrollment_complete) {

                /*
                **  Create Stripe user
                **  @method: @here
                */

                if (determine_portal_enrollment_complete && !this.create_resident_error) this.createStripeResidentUser();

            }

        },

        /*
        **  Step 4
        **  @prop: @here
        */

        create_resident_stripe_complete: {

            immediate: true, // Load on created
            handler(create_resident_stripe_complete) {

                /*
                **  Set the new property resident 
                **  @method: @here
                */

                if (create_resident_stripe_complete && !this.create_resident_error) this.createPropertyResident();

            }

        },

        /*
        **  Step 5
        **  @prop: @here
        */

        create_property_resident_complete: {

            immediate: true, // Load on created
            handler(create_property_resident_complete) {

                /*
                **  Create the resident document
                **  @method: @here
                */

                if (create_property_resident_complete && !this.create_resident_error) this.createResidentDocument();

            }

        },

        /*
        **  Step 6
        **  @prop: @here
        */

        create_resident_complete: {

            immediate: true, // Load on created
            handler(create_resident_complete) {

                /*
                **  Send the referral email
                **  @method: @here
                */

                if (create_resident_complete && !this.create_resident_error) this.sendEnrollmentEmail();

            }

        },

        /*
        **  Step 7
        **  @prop: @here
        */

        email_sent: {

            immediate: true, // Load on created
            handler(email_sent) {

                /*
                **  Update the unit document
                **  @method: @here
                */

                if (email_sent && !this.create_resident_error) this.updateUnitForNewResident();

            }

        },

        /*
        **  Step 8
        **  @prop: @here
        */

        update_new_resident_unit_complete: {

            immediate: true, // Load on created
            handler(update_new_resident_unit_complete) {

                /*
                **  Update the property
                **  @method: @here
                */

                if (update_new_resident_unit_complete && !this.create_resident_error) this.updatePropertyForNewResident();

            }

        },

        /*
        **  Step 9
        **  @prop: @here
        */

        update_new_resident_property_complete: {

            immediate: true, // Load on created
            handler(update_new_resident_property_complete) {

                /*
                **  Update the company
                **  @method: @here
                */

                if (update_new_resident_property_complete && !this.create_resident_error) this.updateCompanyForNewResident();

            }

        },

        /*
        **  Step 10
        **  @prop: @here
        */

        update_new_resident_company_complete: {

            immediate: true, // Load on created
            handler(update_new_resident_company_complete) {

                /*
                **  Complete the event
                **  @method: @here
                */

                if (update_new_resident_company_complete && !this.create_resident_error) this.finishNewResidentEvent();

            }

        }
        
    },

    /*
    **  Methods
    */

    methods: {
        
        createResidentHandler: function() {

            try {
                
                /*
                **  If processing, return
                **  @prop: @here
                */

                if (this.processing) return;

                /*
                **  Set the processing prop
                **  @prop: @here
                */

                this.processing = true;

                /*
                **  Show the spinner
                **  @method: Mixins/Helpers/SpinnerMethods
                */

                this.showSpinner();

                /*
                **  Get the nestd residents portal user
                **  @service: @Firebase/Firestore
                */

                this.setResidentUID();

            } catch (e) {

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Create resident handler.',
                    errorType: 'Application',
                    section: 'Create Resident',
                    uid: 'wPieN2Tkc8TzVE7AOkWJ',
                    error: e
                });

            }

        },

        handleCreateResidentError: function() {

            try {

                /*
                **  Set the processing prop
                **  @prop: @here
                */

                this.processing = false;
    
                /*
                **  Hide the spinner
                **  @method: Mixins/Helpers/SpinnerMethods
                */

                this.hideSpinner();

                /*
                **  Throw error message
                **  @service: SweetAlert2
                */

                this.$swal('Uh Oh!', 'We ran into some problems while trying to create the resident.', 'error');

            } catch (e) {

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Handle an Error When Creating a Resident.',
                    errorType: 'Application',
                    section: 'Create Resident',
                    uid: 'M9WlvxcFXVgpqlsG51Zi',
                    error: e
                });

            }

        },

        setResidentUID: function() {

            try {

                /*
                **  Generate UID
                **  @method: @Mixins/Helpers/GenerateUID
                */

				this.new_resident.id = this.generateUID();

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.set_uid_complete = true;

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.create_resident_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Set the UID When Creating a New Resident.',
                    errorType: 'Application',
                    section: 'Create Resident',
                    uid: 'LUOKdmD7ZQzP6dBLXYc9',
                    error: e
                });

            }

        },

        determinePortalEnrollment: function() {

            try {

                /*
                **  Check if new resident's email has been entered
                */

                if (this.new_resident.attributes.contact_information.email == null || this.new_resident.attributes.contact_information.email.length == 0) {

                    /*
                    **  Set the error props
                    **  @props: @here
                    */

                    this.email.error = true;
                    this.email.title = 'If enrolling in the portal, please provide an valid email address.';

                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.create_resident_error = true;
                
                    return;

                }

                /*
                **  Set the complete prop
                **  @method: @here
                */

                this.determine_portal_enrollment_complete = true;

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.create_resident_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Determine Portal Enrollment When Creating a New Resident.',
                    errorType: 'Application',
                    section: 'Create Resident',
                    uid: 'kaMtVIX7FTZGttoB2Amx',
                    error: e
                });

            }

        },

        createStripeResidentUser: function() {

            try {

                /*
                **  Set the segment name - default to prod
                */

				let apiSegment = 'connect-account/customer';

                /*
                **  If dev, update segment
                */

				if (process.env.VUE_APP_STRIPE_ENV == 'development') apiSegment = 'connect-account-dev/customer';

                /*
                **  Send to API Gateway
                */

				axios({
					method: 'post',
					headers: { 'content-type': 'application/json', 'x-api-key': process.env.VUE_APP_API_GATEWAY_API_KEY },
					url: process.env.VUE_APP_API_GATEWAY_BASE_URL + '/stripe/' + apiSegment,
					data: {
						customer: {
							first_name: this.new_resident.attributes.contact_information.first_name,
							last_name: this.new_resident.attributes.contact_information.last_name,
							email: this.new_resident.attributes.contact_information.email,
							phone_number: this.new_resident.attributes.contact_information.phone_number,
							description: 'Resident of ' + this.property.attributes.address.street_1 + '.',
							metadata: {
								resident_id: this.new_resident.id,
								property_id: this.property.id,
								company_id: this.company.id
							}
						}
					}
				})
				.then(response => {

					if (response.data) {

                        /*
                        **  Add to prop
                        **  @prop: @here
                        */

						this.new_resident.relationships.stripe.customer = response.data;

                        /*
                        **  Set the completed prop
                        **  @prop: @here
                        */

                        this.create_resident_stripe_complete = true;

					}

				})
				.catch(error => {

					if (this.stripe_customer_retry_count == 3) {

                        /*
                        **  Set the prop
                        **  @prop: @here
                        */

                        this.create_resident_error = true;

                        /*
                        **  Reset the prop
                        **  @service: @SweetAlert2
                        */

                        this.stripe_customer_retry_count = 0;

						/*
                        **  Send to Bugsnag
                        **  @service: Bugsnag/BugsnagService
                        */

						this.reportAuthBugsnagError({
							title: 'Create Connected Account Customer at Stripe',
							errorType: 'AWS Lambda',
							section: 'Create Resident',
                            uid: '0y7lrbqmjFMuw41xwhbH',
							error: error
						});

					} else {
 
                        /*
                        **  Increment the counter
                        **  @prop: @here
                        */

						this.stripe_customer_retry_count = this.stripe_customer_retry_count + 1;

                        /*
                        **  Try again to create the customer
                        **  @method: @here
                        */

						this.createStripeResidentUser();

					}

				});

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.create_resident_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Enroll Resident in Portal When Creating a New Resident.',
                    errorType: 'Application',
                    section: 'Create Resident',
                    uid: 'AR58i6LR5hPa5M9cw325',
                    error: e
                });

            }

        },

        setNewResidentProps: function() {

            try {

                /*
                **  Set the props
                **  @props: @here
                */

                this.new_resident.relationships.property = this.property.id;
                this.new_resident.relationships.resident = this.new_resident.id;
                this.new_resident.relationships.company = this.company.id;

                /*
                **  Set the completed prop
                **  @prop: @here
                */

                this.new_resident_props_set = true;

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.create_resident_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Set the New Resident Props When Creating a New Resident.',
                    errorType: 'Application',
                    section: 'Create Resident',
                    uid: 'PlcIAUrXLJFUdzn2rpWp',
                    error: e
                });

            }

        },

        createPropertyResident: function() {

            try {

                /*
                **  Set the enrollment props
                **  @props: @here
                */

                if (this.new_resident.attributes.site_enrollment.enroll_resident) {

                    this.new_resident.attributes.site_enrollment.send_email = true;
                    this.new_resident.attributes.site_enrollment.enrollment_date = moment().unix();

                }

                /*
                **  Create the property resident document
                **  @service: @Firebase/Firestore
                */

                propertiesModel.doc(this.property.id).collection('residents').doc(this.new_resident.id).set(this.new_resident)
                .then(() => {

                    /*
                    **  Set the completed prop
                    **  @prop: @here
                    */

                    this.create_property_resident_complete = true;

                })
                .catch(error => {

                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.create_resident_error = true;

                    /*
                    **  Send to Bugsnag
                    **  @service: Bugsnag/BugsnagService
                    */

                    this.reportAuthBugsnagError({
                        title: 'Unable to Create the Resident Document When Creating a New Resident.',
                        errorType: 'Application',
                        section: 'Create Resident',
                        uid: 'Mur55WRKhYVcmn2curmE',
                        error: error
                    });

                }); 

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.create_resident_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Create the Resident Document When Creating a New Resident.',
                    errorType: 'Application',
                    section: 'Create Resident',
                    uid: 'bwActEYowC2xIVea5pTd',
                    error: e
                });

            }

        },

        createResidentDocument: function() {

            try {

                /*
                **  Create the resident document
                **  @service: @Firebase/Firestore
                */

                residentsModel.doc(this.new_resident.id).set(this.new_resident)
                .then(() => {

                    /*
                    **  Set the completed prop
                    **  @prop: @here
                    */

                    this.create_resident_complete = true;

                })
                .catch(error => {

                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.create_resident_error = true;

                    /*
                    **  Send to Bugsnag
                    **  @service: Bugsnag/BugsnagService
                    */

                    this.reportAuthBugsnagError({
                        title: 'Unable to Create the Resident Document When Creating a New Resident.',
                        errorType: 'Application',
                        section: 'Create Resident',
                        uid: 'SB29a459j7MXjJjhAER5',
                        error: error
                    });

                }); 

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.create_resident_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Create the Resident Document When Creating a New Resident.',
                    errorType: 'Application',
                    section: 'Create Resident',
                    uid: 'x0lj8ludAzN8hW0X6lYM',
                    error: e
                });

            }

        },

        sendEnrollmentEmail: function() {

            try {

                /*
                **  Send the email
                **  @service: @Firebase/Firestore
                */

                emailsModel.add({
					to: this.new_resident.attributes.contact_information.email,
					template: {
						name: 'ResidentWelcomeEmail',
						data: {
							first_name: this.new_resident.attributes.contact_information.first_name,
							referral_code: this.new_resident.attributes.referral.code,
							url: process.env.VUE_APP_RESIDENTS_URL + 'sign-up' + '?ref=' + this.new_resident.attributes.referral.code
						}
					}
				})
                .then(() => {

                    /*
                    **  Update the prop
                    **  @prop: @here
                    */
                   
                    this.email_sent = true;

                })
				.catch(error => {

                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.create_resident_error = true;

   					/*
                    **  Send to Bugsnag
                    **  @service: Bugsnag/BugsnagService
                    */

					this.reportAuthBugsnagError({
						title: 'Send Resident Welcome Email',
						errorType: 'Firestore Database',
						section: 'Create Resident',
                        uid: 'tthhM2BTnK8ZnE3B1YVy',
						error: error
					});

   				});

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.create_resident_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */
            
                this.reportAuthBugsnagError({
                    title: 'Unable to Send the Invite Email from Firestore',
                    errorType: 'Firestore Database',
                    section: 'Create Resident',
                    uid: 'DRzdniqX9OysOFy6Usai',
                    error: e
                });

            }

        },

        updateUnitForNewResident: function() {

            try {

                /*
                **  Update the unit document
                **  @service: @Firebase/Firestore
                */

                propertiesModel.doc(this.property.id).collection('units').doc(this.unit.id).update({
                    'relationships.resident': this.new_resident.id,
                    'attributes.available': false,
                    'attributes.resident.first_name': this.new_resident.attributes.contact_information.first_name,
                    'attributes.resident.last_name': this.new_resident.attributes.contact_information.last_name,
                    'meta.updated_at': moment().unix()
                })
                .then(() => {

                    /*
                    **  Set the completed prop
                    **  @prop: @here
                    */

                    this.update_new_resident_unit_complete = true;

                })
                .catch(error => {
    
                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.create_resident_error = true;

                    /*
                    **  Send to Bugsnag
                    **  @service: Bugsnag/BugsnagService
                    */

                    this.reportAuthBugsnagError({
                        title: 'Update Unit Resident in Firestore',
                        errorType: 'Firestore Database',
                        section: 'Create Resident',
                        uid: '0QMtUjKk9HKwjg3Norlk',
                        error: error
                    });
    
                });

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.create_resident_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Update the Unit When Creating a New Resident.',
                    errorType: 'Application',
                    section: 'Create Resident',
                    uid: '7Az9Yy8IDEyt1JATrufj',
                    error: e
                });

            }

        },

        updatePropertyForNewResident: function() {

            try {

                /*
                **  Calculate the total residents
                */

				let total_residents = this.property.metrics.residents.total + 1;

                /*
                **  Update the property document
                **  @service: @Firebase/Firestore
                */

                propertiesModel.doc(this.property.id).update({

                    'metrics.residents.total': total_residents,
                    'meta.updated_at': moment().unix()
    
                })
                .then(() => {

                    /*
                    **  Set the completed prop
                    **  @prop: @here
                    */

                    this.update_new_resident_property_complete = true;

                })
                .catch(error => {

                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.create_resident_error = true;

                    /*
                    **  Send to Bugsnag
                    **  @service: Bugsnag/BugsnagService
                    */

                    this.reportAuthBugsnagError({
                        title: 'Update Property Resident in Firestore',
                        errorType: 'Firestore Database',
                        section: 'Create Resident',
                        uid: '9Cw0QCe27tShd3XwJfxJ',
                        error: error
                    });

                });

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.create_resident_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Update the Property When Creating a New Resident.',
                    errorType: 'Application',
                    section: 'Create Resident',
                    uid: 'ULKaaFDfi9UAhSgNlCnK',
                    error: e
                });

            }

        },

        updateCompanyForNewResident: function() {

            try {

                /*
                **  Update the company document
                **  @service: @Firebase/Firestore
                */

                companiesModel.doc(this.company.id).update({

                    'metrics.totals.residents': this.company.metrics.totals.residents + 1,
                    'meta.updated_at': moment().unix()
    
                })
                .then(() => {

                    /*
                    **  Set the completed prop
                    **  @prop: @here
                    */

                    this.update_new_resident_company_complete = true;

                })
                .catch(error => {

                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.create_resident_error = true;

                    /*
                    **  Send to Bugsnag
                    **  @service: Bugsnag/BugsnagService
                    */

                    this.reportAuthBugsnagError({
                        title: 'Update Company Metrics in Firestore',
                        errorType: 'Firestore Database',
                        section: 'Create Resident',
                        uid: '45cg5NPnXBeNXY7bBBKH',
                        error: error
                    });

                });

            } catch (e) {

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.create_resident_error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Update the Company When Creating a New Resident.',
                    errorType: 'Application',
                    section: 'Create Resident',
                    uid: 'HumMlCnFsVljfkk50F4N',
                    error: e
                });

            }


        },

        finishNewResidentEvent: function() {

            try {

                /*
                **  Reset the new_resident property
                **  @method: @Repositories/Residents/ResidentsRepository
                */

				this.resetNewResidentProp();

                /*
                **  Close the menu
                **  @method: @AddResidentMenu
                */

				this.closeMenu();

				/*
                **  Hide the spinner
                **  @method: Mixins/Helpers/SpinnerMethods
                */

				this.hideSpinner();

				/*
                **  Throw success message
                **  @service: @SweetAlert2
                */

	    		this.$swal('Success!', 'Your resident has been created.', 'success');

            } catch (e) {

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Complete the New Resident Event When Creating a New Resident.',
                    errorType: 'Application',
                    section: 'Create Resident',
                    uid: '9UVjtErc2BogNvy6k2y8',
                    error: e
                });

            }

        }

    }

}